import React, { useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Divider,
  useColorModeValue,
  Select,
  Button,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { 
  FiDollarSign,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertTriangle,
  FiEye,
  FiRefreshCw,
  FiCreditCard,
  FiTrendingUp
} from 'react-icons/fi';
import { useUserContext } from '../../../context/UserContext';
import { useGetPaymentFlowDetails, useGetPaymentTracking } from '../../../services/payrollService';
import { formatCurrency as formatCurrencyUtil, getCurrencySymbol } from '../../../utils/currency';

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

const PaymentFlowDetails = () => {
  const { user } = useUserContext();
  const [selectedBatchId, setSelectedBatchId] = useState('');
  
  // API hooks
  const { 
    paymentFlowDetails, 
    paymentFlowDetailsIsLoading, 
    paymentFlowDetailsError,
    refetchPaymentFlowDetails 
  } = useGetPaymentFlowDetails(selectedBatchId);

  // Get all batches for dropdown
  const { 
    data: paymentTrackingResponse, 
    isLoading: paymentTrackingLoading, 
    error: paymentTrackingError 
  } = useGetPaymentTracking();

  // Extract batch data for dropdown
  const paymentTrackingData = paymentTrackingResponse?.payment_tracking || [];

  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("gray.50", "gray.700");

  // Use the imported currency utilities

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'success':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      case 'processing':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'Paid';
      case 'COMPLETED': return 'Completed';
      case 'SUCCESS': return 'Success';
      case 'PROCESSING': return 'Processing';
      case 'FAILED': return 'Failed';
      case 'PENDING': return 'Pending';
      case 'PENDING_APPROVAL': return 'Pending Approval';
      case 'REJECTED': return 'Rejected';
      default: return status || 'Unknown';
    }
  };

  // Get payment status icon
  const getPaymentStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <FiCheckCircle color="green" />;
      case 'pending':
        return <FiClock color="orange" />;
      case 'failed':
        return <FiXCircle color="red" />;
      default:
        return <FiAlertTriangle color="gray" />;
    }
  };

  // Get selected batch data from payment tracking
  const selectedBatch = paymentTrackingData.find(batch => batch.batch_id === selectedBatchId);
  
  // Use payment flow details if available, otherwise use payment tracking data
  const summary = paymentFlowDetails?.summary || (selectedBatch ? {
    total_employees: selectedBatch.employee_count,
    net_salaries_sent_to_flutterwave: selectedBatch.payment_progress?.total_amount || 0,
    processing_fees_collected: 0, // Not available in payment tracking data
    settlement_amount_remaining: selectedBatch.payment_progress?.settlement_amount_remaining || 0
  } : null);
  
  const walletStatus = paymentFlowDetails?.wallet_status;
  const employeeDetails = paymentFlowDetails?.employee_details || selectedBatch?.employee_details || [];
  const flutterwaveDetails = paymentFlowDetails?.flutterwave_details;

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="blue.600">
              Payment Flow Details
            </Heading>
            <Text color="gray.600">
              Track detailed payment flow and settlement information
            </Text>
          </VStack>
          <HStack spacing={2}>
            <Select
              placeholder="Select batch to view details"
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              w="300px"
              isLoading={paymentTrackingLoading}
            >
              {paymentTrackingData.map((batch) => (
                <option key={batch.batch_id} value={batch.batch_id}>
                  {batch.batch_name} - {batch.batch_id}
                </option>
              ))}
            </Select>
            <Tooltip label="Refresh data">
              <IconButton
                icon={<FiRefreshCw />}
                onClick={refetchPaymentFlowDetails}
                isLoading={paymentFlowDetailsIsLoading}
                size="sm"
              />
            </Tooltip>
          </HStack>
        </Flex>

        <Divider />

        {!selectedBatchId ? (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>Select a batch</AlertTitle>
              <AlertDescription>
                Please select a payroll batch from the dropdown above to view payment flow details.
              </AlertDescription>
            </Box>
          </Alert>
        ) : paymentFlowDetailsIsLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" />
          </Flex>
        ) : paymentFlowDetailsError ? (
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Error loading payment flow details!</AlertTitle>
              <AlertDescription>
                {paymentFlowDetailsError}
              </AlertDescription>
            </Box>
          </Alert>
        ) : (paymentFlowDetails || selectedBatch) ? (
          <>
            {/* Batch Information */}
            <Box p={4} bg={cardBg} borderRadius="md">
              <HStack justify="space-between" mb={4}>
                <VStack align="start" spacing={1}>
                  <Heading size="md" color="gray.700">
                    {selectedBatch?.batch_name || paymentFlowDetails?.batch_name}
                  </Heading>
                  <Text color="gray.600">Batch ID: {selectedBatch?.batch_id || paymentFlowDetails?.batch_id}</Text>
                </VStack>
                <VStack align="end" spacing={1}>
                  <Badge colorScheme={getStatusColor(selectedBatch?.payment_status || paymentFlowDetails?.status)} size="lg">
                    {selectedBatch?.payment_status || paymentFlowDetails?.status}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    {selectedBatch?.items?.[0]?.currency || 'NGN'}
                  </Text>
                </VStack>
              </HStack>
              
              <Text fontSize="sm" color="gray.600">
                Status Description: <Badge colorScheme="blue">{selectedBatch?.status_description || paymentFlowDetails?.payment_flow}</Badge>
              </Text>
            </Box>

            {/* Summary Statistics */}
            {summary && (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Total Employees</StatLabel>
                    <StatNumber color="blue.600">
                      <HStack>
                        <FiUsers />
                        <Text>{summary.total_employees}</Text>
                      </HStack>
                    </StatNumber>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Net Salaries Sent</StatLabel>
                    <StatNumber color="green.600">
                      <Text>{formatCurrencyWithCode(summary.net_salaries_sent_to_flutterwave, selectedBatch?.items?.[0]?.currency || 'NGN')}</Text>
                    </StatNumber>
                    <StatHelpText>To Payment Gateway</StatHelpText>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Processing Fees</StatLabel>
                    <StatNumber color="purple.600">
                      <Text>{formatCurrencyWithCode(summary.processing_fees_collected, selectedBatch?.items?.[0]?.currency || 'NGN')}</Text>
                    </StatNumber>
                    <StatHelpText>Collected</StatHelpText>
                  </Stat>
                </Box>

                <Box p={4} bg={cardBg} borderRadius="md">
                  <Stat>
                    <StatLabel>Settlement Remaining</StatLabel>
                    <StatNumber color="orange.600">
                      <Text>{formatCurrencyWithCode(summary.settlement_amount_remaining, selectedBatch?.items?.[0]?.currency || 'NGN')}</Text>
                    </StatNumber>
                    <StatHelpText>Pending settlement</StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>
            )}

            {/* Wallet Status */}
            {walletStatus && (
              <Box p={4} bg={cardBg} borderRadius="md">
                <Heading size="md" mb={4} color="gray.700">
                  Wallet Status
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Available Balance</Text>
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      {formatCurrencyWithCode(walletStatus.available_balance ?? walletStatus.balance ?? walletStatus.current_balance, selectedBatch?.items?.[0]?.currency || 'NGN')}
                    </Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Net Salaries Paid</Text>
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      {formatCurrencyWithCode(walletStatus.net_salaries_paid, selectedBatch?.items?.[0]?.currency || 'NGN')}
                    </Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Settlement Pending</Text>
                    <Text fontSize="lg" fontWeight="bold" color="orange.600">
                      {formatCurrencyWithCode(walletStatus.settlement_pending, selectedBatch?.items?.[0]?.currency || 'NGN')}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </Box>
            )}

            {/* Employee Payment Details */}
            {employeeDetails.length > 0 && (
              <Box>
                <Heading size="md" mb={4} color="gray.700">
                  Employee Payment Details
                </Heading>
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Employee</Th>
                        <Th>Net Salary Sent</Th>
                        <Th>Settlement Amount</Th>
                        <Th>Payment Status</Th>
                        <Th>Refund Amount</Th>
                        <Th>Reference</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {employeeDetails.map((employee, index) => (
                        <Tr key={index}>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">{employee.employee_name}</Text>
                              <Text fontSize="sm" color="gray.600">
                                ID: {employee.employee_id}
                              </Text>
                            </VStack>
                          </Td>
                          <Td color="green.600" fontWeight="medium">
                            {formatCurrencyWithCode(employee.net_salary_sent || employee.net_salary, employee.currency)}
                          </Td>
                          <Td color="orange.600" fontWeight="medium">
                            {formatCurrencyWithCode(employee.settlement_amount || 0, employee.currency)}
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              <HStack>
                                {getPaymentStatusIcon(employee.payment_status)}
                                <Badge colorScheme={getStatusColor(employee.payment_status)}>
                                  {getStatusText(employee.payment_status)}
                                </Badge>
                              </HStack>
                              {employee.failure_reason && (
                                <Text fontSize="xs" color="red.600">
                                  {employee.failure_reason}
                                </Text>
                              )}
                            </VStack>
                          </Td>
                          <Td>
                            {employee.payment_status?.toLowerCase() === 'failed' && employee.refund_amount ? (
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="600" color="red.600">
                                  {formatCurrencyWithCode(employee.refund_amount, employee.currency)}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Will be refunded
                                </Text>
                              </VStack>
                            ) : (
                              <Text fontSize="sm" color="gray.400">—</Text>
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.600">
                              {employee.payment_reference || 'N/A'}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* Payment Transaction Details */}
            {(flutterwaveDetails || selectedBatch) && (
              <Box p={4} bg={cardBg} borderRadius="md">
                <Heading size="md" mb={4} color="gray.700">
                  Payment Transaction Details
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Employee Payments Batch ID</Text>
                    <Text fontWeight="medium">{flutterwaveDetails?.employee_payments_batch_id || selectedBatch?.flutterwave_batch_id || 'N/A'}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Processing Fee Transfer ID</Text>
                    <Text fontWeight="medium">{flutterwaveDetails?.processing_fee_transfer_id || selectedBatch?.processing_fee_transfer_id || 'N/A'}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Payment Reference</Text>
                    <Text fontWeight="medium">{selectedBatch?.payment_reference || 'N/A'}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Created At</Text>
                    <Text fontWeight="medium">{selectedBatch?.created_at ? new Date(selectedBatch.created_at).toLocaleString() : 'N/A'}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Executed At</Text>
                    <Text fontWeight="medium">{selectedBatch?.executed_at ? new Date(selectedBatch.executed_at).toLocaleString() : 'Not executed'}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">Created By</Text>
                    <Text fontWeight="medium">
                      {typeof selectedBatch?.created_by === 'object' ? selectedBatch.created_by?.name || 'N/A' : selectedBatch?.created_by || 'N/A'}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </Box>
            )}

            {/* Payment Progress Details */}
            {selectedBatch?.payment_progress && (
              <Box p={4} bg={cardBg} borderRadius="md">
                <Heading size="md" mb={4} color="gray.700">
                  Payment Progress Details
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text>Total Amount:</Text>
                      <Text fontWeight="bold" color="blue.600">
                        {formatCurrencyWithCode(selectedBatch.payment_progress.total_amount, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Net Salaries Paid:</Text>
                      <Text fontWeight="bold" color="green.600">
                        {formatCurrencyWithCode(selectedBatch.payment_progress.net_salaries_paid, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Processing Fees Paid:</Text>
                      <Text fontWeight="bold" color="purple.600">
                        {formatCurrencyWithCode(selectedBatch.payment_progress.processing_fees_paid, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Settlement Amount Remaining:</Text>
                      <Text fontWeight="bold" color="orange.600">
                        {formatCurrencyWithCode(selectedBatch.payment_progress.settlement_amount_remaining, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                  </VStack>
                </SimpleGrid>
              </Box>
            )}

            {/* Breakdown Details */}
            {summary?.breakdown && (
              <Box p={4} bg={cardBg} borderRadius="md">
                <Heading size="md" mb={4} color="gray.700">
                  Payment Breakdown
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text>Tax Amount:</Text>
                      <Text fontWeight="bold" color="red.600">
                        {formatCurrencyWithCode(summary.breakdown.tax_amount, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Deductions:</Text>
                      <Text fontWeight="bold" color="orange.600">
                        {formatCurrencyWithCode(summary.breakdown.deductions, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Transfer Charges:</Text>
                      <Text fontWeight="bold" color="purple.600">
                        {formatCurrencyWithCode(summary.breakdown.flutterwave_fees, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Processing Fees:</Text>
                      <Text fontWeight="bold" color="teal.600">
                        {formatCurrencyWithCode(summary.breakdown.processing_fees, selectedBatch?.items?.[0]?.currency || 'NGN')}
                      </Text>
                    </HStack>
                  </VStack>
                </SimpleGrid>
              </Box>
            )}
          </>
        ) : (
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <AlertTitle>No payment flow details found</AlertTitle>
              <AlertDescription>
                No payment flow details available for the selected batch.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default PaymentFlowDetails;